/**
 * InlineForm component.
 *
 * Renders a structured data collection form inline within the chat
 * message area. The backend sends a form specification with field
 * definitions, and the widget renders the appropriate input components.
 *
 * Key behaviors:
 * - Pre-populated fields from conversation context (all editable)
 * - Validation before submission (required fields, phone format, NRIC format)
 * - NRIC field cleared on submission (never cached client-side)
 * - Entity-branded styling (input borders, button colors)
 * - Keyboard accessible (Tab between fields, Enter to submit)
 * - Cancel option to dismiss and continue conversationally
 */

import type {
  ResolvedWidgetConfig,
  FormFieldDefinition,
  FormMessage,
  FormCallbacks,
  FormSubmitEvent,
  FormStatus,
  FieldValidationError,
} from "../types";

import { TextField } from "./form/TextField";
import { PhoneField } from "./form/PhoneField";
import { DateField } from "./form/DateField";
import { isFutureDate } from "../utils/dateBounds";
import { DropdownField } from "./form/DropdownField";
import { TextareaField } from "./form/TextareaField";
import { NRICField } from "./form/NRICField";

/** Common interface for all field components */
interface FieldComponent {
  getElement(): HTMLElement;
  getValue(): string;
  setError(message: string): void;
  clearError(): void;
  setDisabled(disabled: boolean): void;
  focus(): void;
}

/** Singapore phone number pattern: 8 digits starting with 8 or 9 */
const SG_PHONE_REGEX = /^[89]\d{7}$/;

/** Malaysia phone number: 9-10 digits, mobile starts with 1 */
const MY_PHONE_REGEX = /^1\d{8,9}$/;

/** General phone number: 8-15 digits */
const GENERAL_PHONE_REGEX = /^\d{8,15}$/;

/** NRIC/FIN pattern: S/T/F/G/M + 7 digits + 1 letter */
const NRIC_REGEX = /^[STFGM]\d{7}[A-Z]$/;

/** Passport pattern: 6-12 alphanumeric characters */
const PASSPORT_REGEX = /^[A-Z0-9]{6,12}$/;

/** ISO 8601 date pattern: YYYY-MM-DD */
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export interface InlineFormOptions {
  config: ResolvedWidgetConfig;
  message: FormMessage;
  callbacks: FormCallbacks;
}

export class InlineForm {
  private readonly element: HTMLElement;
  private readonly fieldComponents: Map<string, FieldComponent> = new Map();
  private readonly nricFields: Set<string> = new Set();
  private readonly fieldDefinitions: FormFieldDefinition[];
  private readonly submitBtn: HTMLButtonElement;
  private readonly cancelBtn: HTMLButtonElement;
  private readonly apiBaseUrl: string;
  private status: FormStatus = "active";

  constructor(options: InlineFormOptions) {
    const { config, message, callbacks } = options;

    this.fieldDefinitions = message.fields;
    // Derive REST base URL from the widget's apiEndpoint for sub-component fetches
    try {
      const parsed = new URL(config.apiEndpoint);
      this.apiBaseUrl = `${parsed.protocol}//${parsed.host}`;
    } catch {
      this.apiBaseUrl = "";
    }

    this.element = document.createElement("div");
    this.element.className = "hana-form";
    this.element.setAttribute("role", "form");
    this.element.setAttribute(
      "aria-label",
      message.title ?? "Data collection form",
    );

    // Title (optional)
    if (message.title) {
      const titleEl = document.createElement("div");
      titleEl.className = "hana-form__title";
      titleEl.textContent = message.title;
      this.element.appendChild(titleEl);
    }

    // Fields container
    const fieldsEl = document.createElement("div");
    fieldsEl.className = "hana-form__fields";

    for (const fieldDef of message.fields) {
      const component = this.createFieldComponent(fieldDef);
      this.fieldComponents.set(fieldDef.name, component);
      fieldsEl.appendChild(component.getElement());
    }

    this.element.appendChild(fieldsEl);

    // Actions container
    const actionsEl = document.createElement("div");
    actionsEl.className = "hana-form__actions";

    // Submit button
    this.submitBtn = document.createElement("button");
    this.submitBtn.type = "button";
    this.submitBtn.className = "hana-form__btn hana-form__btn--submit";
    this.submitBtn.textContent = message.submitLabel ?? "Submit";
    this.submitBtn.setAttribute(
      "aria-label",
      message.submitLabel ?? "Submit form",
    );
    this.submitBtn.addEventListener("click", () => {
      this.handleSubmit(config, callbacks);
    });
    actionsEl.appendChild(this.submitBtn);

    // Cancel button
    this.cancelBtn = document.createElement("button");
    this.cancelBtn.type = "button";
    this.cancelBtn.className = "hana-form__btn hana-form__btn--cancel";
    this.cancelBtn.textContent = message.cancelLabel ?? "Cancel";
    this.cancelBtn.setAttribute(
      "aria-label",
      message.cancelLabel ?? "Cancel form",
    );
    this.cancelBtn.addEventListener("click", () => {
      this.handleCancel(callbacks);
    });
    actionsEl.appendChild(this.cancelBtn);

    this.element.appendChild(actionsEl);

    // Keyboard: Enter on the last field submits the form
    this.element.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        const target = e.target as HTMLElement;
        // Do not intercept Enter in textareas (they need newlines)
        if (target.tagName === "TEXTAREA") return;
        e.preventDefault();
        this.handleSubmit(config, callbacks);
      }
    });
  }

  getElement(): HTMLElement {
    return this.element;
  }

  getStatus(): FormStatus {
    return this.status;
  }

  /**
   * Get the current values of all form fields as a key-value map.
   * Includes country_code and id_type from specialized field components.
   */
  getValues(): Record<string, string> {
    const values: Record<string, string> = {};
    for (const [name, component] of this.fieldComponents) {
      values[name] = component.getValue();
      // Include country_code from phone fields
      if (component instanceof PhoneField) {
        values["country_code"] = component.getCountryCode();
      }
      // Include id_type from NRIC fields
      if (component instanceof NRICField) {
        values["id_type"] = component.getIdType();
      }
    }
    return values;
  }

  /**
   * Validate all fields and return a list of errors.
   * Returns an empty array if all fields are valid.
   */
  validate(): FieldValidationError[] {
    const errors: FieldValidationError[] = [];

    for (const fieldDef of this.fieldDefinitions) {
      const component = this.fieldComponents.get(fieldDef.name);
      if (!component) continue;

      const value = component.getValue();
      component.clearError();

      // Required check
      if (fieldDef.required && value.length === 0) {
        const error = {
          field: fieldDef.name,
          message: `${fieldDef.label} is required`,
        };
        errors.push(error);
        component.setError(error.message);
        continue;
      }

      // Skip further validation if field is empty and not required
      if (value.length === 0) continue;

      // Type-specific validation
      switch (fieldDef.type) {
        case "phone": {
          // For phone fields, extract just the digits (strip prefix)
          const phoneComponent = component as PhoneField;
          const rawDigits = phoneComponent.getRawValue();
          if (rawDigits.length > 0) {
            const countryCode = phoneComponent.getCountryCode();
            let isValid: boolean;
            let errorMsg: string;
            if (countryCode === "+65") {
              isValid = SG_PHONE_REGEX.test(rawDigits);
              errorMsg =
                "Please enter a valid Singapore mobile number (8 digits starting with 8 or 9)";
            } else if (countryCode === "+60") {
              isValid = MY_PHONE_REGEX.test(rawDigits);
              errorMsg =
                "Please enter a valid Malaysia mobile number (9-10 digits starting with 1)";
            } else {
              isValid = GENERAL_PHONE_REGEX.test(rawDigits);
              errorMsg = "Please enter a valid mobile number (8-15 digits)";
            }
            if (!isValid) {
              const error = { field: fieldDef.name, message: errorMsg };
              errors.push(error);
              component.setError(error.message);
            }
          }
          break;
        }

        case "nric": {
          const nricComponent = component as NRICField;
          const idType = nricComponent.getIdType();
          const isValidId =
            idType === "Passport"
              ? PASSPORT_REGEX.test(value)
              : NRIC_REGEX.test(value);
          if (!isValidId) {
            const error = {
              field: fieldDef.name,
              message:
                idType === "Passport"
                  ? "Please enter a valid passport number (6-12 alphanumeric characters)"
                  : "Please enter a valid NRIC/FIN (e.g., S1234567D)",
            };
            errors.push(error);
            component.setError(error.message);
          }
          break;
        }

        case "date": {
          if (!DATE_REGEX.test(value)) {
            const error = {
              field: fieldDef.name,
              message: "Please enter a valid date",
            };
            errors.push(error);
            component.setError(error.message);
          } else if (isFutureDate(value)) {
            // Date of Birth cannot be in the future. Mirrors the
            // backend `_parse_dob` guard (rejects parsed > today).
            const error = {
              field: fieldDef.name,
              message: "Date of birth cannot be in the future",
            };
            errors.push(error);
            component.setError(error.message);
          }
          break;
        }
      }
    }

    return errors;
  }

  private createFieldComponent(fieldDef: FormFieldDefinition): FieldComponent {
    const onChange = (_name: string, _value: string) => {
      // Validation errors clear on input (handled in each field component)
    };

    switch (fieldDef.type) {
      case "phone":
        return new PhoneField({
          field: fieldDef,
          onChange,
          apiBaseUrl: this.apiBaseUrl,
        });

      case "date":
        return new DateField({ field: fieldDef, onChange });

      case "dropdown":
        return new DropdownField({ field: fieldDef, onChange });

      case "textarea":
        return new TextareaField({ field: fieldDef, onChange });

      case "nric": {
        this.nricFields.add(fieldDef.name);
        return new NRICField({ field: fieldDef, onChange });
      }

      case "text":
      default:
        return new TextField({ field: fieldDef, onChange });
    }
  }

  private handleSubmit(
    config: ResolvedWidgetConfig,
    callbacks: FormCallbacks,
  ): void {
    if (this.status !== "active") return;

    // Validate
    const errors = this.validate();
    if (errors.length > 0) {
      // Focus the first errored field
      const firstError = errors[0];
      const component = this.fieldComponents.get(firstError.field);
      component?.focus();
      return;
    }

    // Collect values
    const data = this.getValues();

    // Mark as submitted
    this.status = "submitted";
    this.setAllFieldsDisabled(true);
    this.submitBtn.disabled = true;
    this.cancelBtn.disabled = true;

    // Clear NRIC fields from the DOM inputs (security: never cache)
    for (const nricName of this.nricFields) {
      const component = this.fieldComponents.get(nricName);
      if (component && component instanceof NRICField) {
        component.clearValue();
      }
    }

    // Replace actions with confirmation
    const actionsEl = this.element.querySelector(".hana-form__actions");
    if (actionsEl) {
      const statusEl = document.createElement("div");
      statusEl.className = "hana-form__status hana-form__status--submitted";
      statusEl.textContent = "Form submitted";
      statusEl.setAttribute("role", "status");
      actionsEl.replaceWith(statusEl);
    }

    // Emit submit event
    const event: FormSubmitEvent = {
      data,
      entityId: config.entityId,
      timestamp: new Date().toISOString(),
    };
    callbacks.onSubmit(event);
  }

  private handleCancel(callbacks: FormCallbacks): void {
    if (this.status !== "active") return;

    this.status = "cancelled";
    this.setAllFieldsDisabled(true);
    this.submitBtn.disabled = true;
    this.cancelBtn.disabled = true;

    // Clear NRIC fields (security: never cache, even on cancel)
    for (const nricName of this.nricFields) {
      const component = this.fieldComponents.get(nricName);
      if (component && component instanceof NRICField) {
        component.clearValue();
      }
    }

    // Replace actions with cancellation message
    const actionsEl = this.element.querySelector(".hana-form__actions");
    if (actionsEl) {
      const statusEl = document.createElement("div");
      statusEl.className = "hana-form__status hana-form__status--cancelled";
      statusEl.textContent = "Form cancelled";
      statusEl.setAttribute("role", "status");
      actionsEl.replaceWith(statusEl);
    }

    callbacks.onCancel();
  }

  private setAllFieldsDisabled(disabled: boolean): void {
    for (const component of this.fieldComponents.values()) {
      component.setDisabled(disabled);
    }
  }
}
